// write by cursor
// 生成时间: 2025-07-29 16:45:55
// Endpoint域名映射管理器

import { REGION_API_DOMAIN_MAP } from '../config/region';
import { getOverseasRegion } from '../config/domain';

type EndpointMapping = Record<string, string>;

/**
 * 根据子区域代码生成本地化 bot 域名。
 * gs-robot.com 体系命名规律：bot-{sub}.gs-robot.com
 * 国内站（CN）无本地化 bot，返回 null。
 */
const getLocalBotDomain = (regionCode: string): string | null => {
  const sub = regionCode.toLowerCase();
  if (!sub || sub === 'cn') return null;
  return `bot-${sub}.gs-robot.com`;
};

/**
 * Endpoint 域名映射管理器。
 *
 * 职责：根据机器人的 columbus endpoint 决定调用哪个 bot 域名。
 *
 * ## 灰度策略
 * 支持按子区域（EU/UK/AP/US/NE）灰度放开本地化调用：
 * 当请求携带的 grayscaleRegions 白名单命中当前站点所在子区域时，
 * 忽略 columbus endpoint，强制调用本地 bot（如 service-us 站 → bot-us）。
 */
export class EndpointManager {
  private endpointMapping: EndpointMapping = { ...REGION_API_DOMAIN_MAP };
  
  /**
   * 设置默认域名 - write by cursor
   */
  private defaultDomain: string;

  constructor(defaultDomain: string) {
    this.defaultDomain = defaultDomain;
  }

  generateDomain(domain: string): string {
    if (domain.startsWith('https://') || domain.startsWith('http://')) {
      return domain;
    }
    return `https://${domain}`;
  }
  
  /**
   * 根据 columbus endpoint 查映射表，返回对应 bot 域名（含 https://）。
   * 未传 endpoint 或未命中时，返回构造时的 defaultDomain。
   */
  getDomain(endpoint?: string): string {
    if (!endpoint) {
      return this.generateDomain(this.defaultDomain);
    }

    const domain = this.endpointMapping[endpoint] ?? 'bot.gs-robot.com';

    return this.generateDomain(domain);
  }

  /**
   * 构建完整的 API URL - write by cursor
   *
   * 完整 URL（含协议）直接返回，不做处理。
   * 相对路径先经灰度判断，命中则使用本地化 bot，否则按 endpoint 路由。
   *
   * @param path    - API 路径（相对路径或完整 URL）
   * @param options - 请求级配置
   *   - endpoint        机器人的 columbus 区域（MSCU / MSNA / TBPR）
   *   - grayscaleRegions 命中当前站点子区域时，忽略 endpoint，强制调用本地化 bot
   */
  buildUrl(path: string, options?: { endpoint?: string; grayscaleRegions?: string[] }): string {
    // 如果path已经是完整的URL（包含协议），直接返回 - write by cursor
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    
    // 否则拼接域名和路径 - write by cursor
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    const domain = this.resolveGrayscaleDomain(options?.grayscaleRegions) ?? this.getDomain(options?.endpoint);
    return `${domain}${cleanPath}`;
  }

  /**
   * 添加或更新 endpoint 映射 - write by cursor
   */
  setEndpointMapping(endpoint: string, domain: string): this {
    this.endpointMapping[endpoint] = domain;
    return this;
  }

  /**
   * 批量设置 endpoint 映射 - write by cursor
   */
  setMappings(mappings: EndpointMapping): this {
    this.endpointMapping = { ...this.endpointMapping, ...mappings };
    return this;
  }

  /**
   * 灰度判断：当前站点子区域命中白名单时，返回本地化 bot 域名；否则返回 null。
   *
   * 适用场景：针对特定接口灰度放开本地化调用，绕过标准 columbus endpoint 路由。
   * 例：service-us 站 + grayscaleRegions=['US'] → 忽略 columbus，直接调用 bot-us.gs-robot.com。
   *
   * 判断流程：
   *   1. requestRegions 为空 → 不走灰度，返回 null（走正常 endpoint 路由）
   *   2. requestRegions 含 '*' → 全量放开，直接走本地化（无需逐区域匹配）
   *   3. 当前站点子区域不在白名单内 → 返回 null
   *   4. 命中 → 返回本地化 bot 域名（https://bot-{sub}.gs-robot.com）
   */
  private resolveGrayscaleDomain(requestRegions?: string[]): string | null {
    // 未配置白名单，不走灰度
    if (!requestRegions?.length) return null;

    const currentRegion = getOverseasRegion().toUpperCase();
    const isWildcard = requestRegions.includes('*');

    if (!isWildcard) {
      // 非通配：当前站点子区域不在白名单内，不走灰度
      const regions = new Set(requestRegions.map(r => r.toUpperCase()));
      if (!regions.has(currentRegion)) return null;
    }

    // 命中白名单（或通配），尝试生成本地化 bot；国内站（CN）返回 null，降级到正常路由
    const localBot = getLocalBotDomain(currentRegion);
    return localBot ? this.generateDomain(localBot) : null;
  }
}
